'use client'

import { useState } from "react"
import dynamic from "next/dynamic"
import { useRouter } from "next/navigation"
import AppButton from "@/app/components/atomic/molecules/AppButton"
import AppIcon from "@/app/components/atomic/AppIcon"
import CardSelect from "@/app/components/atomic/CardSelect"
import QuestionLabel from "@/app/components/atomic/molecules/QuestionLabel"
import StepsBreadcrumb from "@/app/components/atomic/StepsBreadcrumb"
import { buildHealthBreadcrumbs, STEP_FAMILY_TYPE } from "@/app/components/healthComparator/health-form-steps.constants"
import { FAMILY_TYPE_OPTIONS, MAX_AGE, MIN_AGE } from "@/app/components/healthComparator/health-form.constants"
import { landingHandoffUrl, resolveLandingQuoteToken } from "@/app/components/healthComparator/health-landing-handoff"
import { useTracking } from "@/app/contexts/tracking-context"
import { HealthTrackingEnum } from "@/app/enums/HealthTrackingEnum"
import { useHealthLandingStore } from "@/app/stores/health/health-landing-store"
import { isBirthdateWithinAges, maxBirthDateForAge, minBirthDateForAge } from "@/app/utils/health/health-date-utils"

const AppDatePicker = dynamic(() => import("@/app/components/atomic/molecules/AppDatePicker"))

const ANSWER_REQUIRED_MESSAGE = "Veuillez répondre à cette question."
const BIRTHDATE_RANGE_MESSAGE = `L'âge doit être entre ${MIN_AGE} et ${MAX_AGE} ans`
const QUOTE_FAILURE_MESSAGE = "Une erreur est survenue, veuillez réessayer."
const BIRTHDATE_INPUT_ID = "health-quick-start-birthdate"

export default function HealthQuickStartWidget({ pageToken }: { pageToken?: string | null }) {
    const router = useRouter()
    const { track } = useTracking()
    const originRoute = useHealthLandingStore((state) => state.originRoute)

    const [birthdate, setBirthdate] = useState("")
    const [familyType, setFamilyType] = useState<number | null>(null)
    const [showErrors, setShowErrors] = useState(false)
    const [busy, setBusy] = useState(false)
    const [quoteFailed, setQuoteFailed] = useState(false)

    const isBirthdateValid = isBirthdateWithinAges(birthdate, MIN_AGE, MAX_AGE)
    const birthdateError = showErrors && !isBirthdateValid
        ? birthdate ? BIRTHDATE_RANGE_MESSAGE : ANSWER_REQUIRED_MESSAGE
        : undefined

    const handleSubmit = async () => {
        if (busy) return

        if (!isBirthdateValid || familyType === null) {
            setShowErrors(true)
            return
        }

        // The card stands in for the funnel's first two steps, whose display events never fire.
        track({ event: HealthTrackingEnum.FunnelAccess })
        track({ event: HealthTrackingEnum.ProfileSelection })
        track({ event: HealthTrackingEnum.BirthDate })
        setBusy(true)
        setQuoteFailed(false)

        const token = await resolveLandingQuoteToken(originRoute, pageToken)
        if (!token) {
            setBusy(false)
            setQuoteFailed(true)
            return
        }
        router.push(landingHandoffUrl(token, { familyType, birthdate }))
    }

    return (
        <div
            className="flex flex-col gap-[22px] rounded-3xl bg-white p-5 shadow-[0_1px_2px_-1px_rgba(0,0,0,0.1),0_1px_3px_0_rgba(0,0,0,0.1),0_1px_4px_0_rgba(0,0,0,0.25)] md:p-7"
            data-test="health-quick-start"
        >
            <div className="flex items-center justify-between gap-3">
                <h2 className="block px-0 text-[22px] leading-tight md:text-2xl">Ma comparaison gratuite</h2>
                <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-pastel-yellow px-3 py-1 text-[13px] font-bold text-pale">
                    <AppIcon iconClassName="fa-clock" type="regular" size="sm" />2 min
                </span>
            </div>

            <StepsBreadcrumb steps={buildHealthBreadcrumbs()} currentStep={STEP_FAMILY_TYPE} />

            <div className="flex flex-col gap-3">
                <label htmlFor={BIRTHDATE_INPUT_ID}>
                    <QuestionLabel number={1}>Quelle est votre date de naissance ?</QuestionLabel>
                </label>
                <div data-test="quick-start-birthdate">
                    <AppDatePicker
                        id={BIRTHDATE_INPUT_ID}
                        variant="travel"
                        birthdate
                        displayIcon
                        placeholder="JJ/MM/AAAA"
                        value={birthdate}
                        onChange={setBirthdate}
                        minDate={minBirthDateForAge(MAX_AGE)}
                        maxDate={maxBirthDateForAge(MIN_AGE)}
                        error={birthdateError ? { message: birthdateError } : undefined}
                    />
                </div>
            </div>

            <div className="flex flex-col gap-3">
                <QuestionLabel number={2}>Qui souhaitez-vous assurer ?</QuestionLabel>
                <CardSelect<number>
                    variant="travel"
                    size="sm"
                    options={FAMILY_TYPE_OPTIONS}
                    value={familyType}
                    onChange={setFamilyType}
                    testIdPrefix="quick-start-family-type"
                    containerClassName="grid grid-cols-1 gap-2.5 sm:grid-cols-2"
                    error={showErrors && familyType === null ? { message: ANSWER_REQUIRED_MESSAGE } : undefined}
                />
            </div>

            <div className="flex flex-col items-center gap-3">
                <div className="w-full [&_div]:text-[18px] md:[&_div]:text-[20px] [&_i]:text-[22px]">
                    <AppButton
                        wFull
                        size="lg"
                        onClick={handleSubmit}
                        loading={busy}
                        arrowPosition="before"
                        dataTest="health-quick-start-cta"
                    >
                        Je compare les mutuelles
                    </AppButton>
                </div>

                {quoteFailed && (
                    <p className="m-0 text-center text-[12px] text-red-600" role="alert" data-test="health-quick-start-failure">
                        {QUOTE_FAILURE_MESSAGE}
                    </p>
                )}

                <p className="m-0 text-center text-[13px] leading-snug text-[#71717A]">
                    <i className="fa-regular fa-lock mr-2" aria-hidden />
                    Gratuit, sans engagement
                </p>
            </div>
        </div>
    )
}
